/**
 *  All api related to system setting
 */

import _ from 'lodash'
import axios from 'axios'
import { OmnitechApi } from '../../api'
import { cancelRequest } from '../../helpers'
import { isErrorExist, convertResponseToErrorObject } from '../../omnitech-error'

// - fetch cart shipment courier services
export function fetchCartShipmentAvailablePickupStores({
  shipmentId,
  params = {},
  ...otherOptions
}) {
  const source = axios.CancelToken.source()
  cancelRequest.add(
    'fetchCartShipmentAvailablePickupStores',
    () => source.cancel('fetchCartShipmentAvailablePickupStores: Operation cancelled by user')
  )

  const {
    page = 1,
    ...others
  } = params

  return OmnitechApi
    .request({
      endpoint: `cart_shipments/${shipmentId}/available_pickup_stores`,
      method: 'get',
      params: {
        page,
        ...others,
      },
      cancelToken: source.token,
      ...otherOptions
    })
    .catch(error => {
      if (cancelRequest.isCancel(error)) {
        throw cancelRequest.getErrorObject()
      }
      throw convertResponseToErrorObject(_.get(error, 'response.data'), { useResponseMessage: true })
    })
    .then(response => {
      if (isErrorExist(response.data)) throw convertResponseToErrorObject(response.data, { useResponseMessage: true })
      return response.data
    })
    .then(({ data }) => ({
      stores: data.stores,
      next: _.get(data, 'storesPagination.nextPage') > page
        ? () => fetchCartShipmentAvailablePickupStores({
          shipmentId,
          params: {
            page: page + 1,
            ...others
          },
          ...otherOptions
        })
        : null,
      all: page === 1 && _.get(data, 'storesPagination.nextPage') > 1
        ? () => Promise.all(
          _.times(
            _.get(data, 'storesPagination.totalPages') - 1,
            (index) => fetchCartShipmentAvailablePickupStores({
              shipmentId,
              params: {
                pageCountless: true,
                page: index + 2,
                ...others
              },
              ...otherOptions
            }),
          ),
        )
        : null,
      count: _.get(data, 'storesPagination.totalCount'),
    }))
}

// - fetch cart shipment courier services
export function fetchCartShipmentCourierServices({
  shipmentId,
  params = {},
  ...otherOptions
}) {
  const source = axios.CancelToken.source()
  cancelRequest.add(
    'fetchCartShipmentCourierServices',
    () => source.cancel('fetchCartShipmentCourierServices: Operation cancelled by user')
  )

  const {
    page = 1,
    ...others
  } = params

  return OmnitechApi
    .request({
      endpoint: `cart_shipments/${shipmentId}/available_courier_services`,
      method: 'get',
      params: {
        // CP-20210205: Use old schemaVersion until we migrate to cart/order line property
        schemaVersion: '2020-07-20',
        page,
        ...others
      },
      cancelToken: source.token,
      ...otherOptions
    })
    .catch(error => {
      if (cancelRequest.isCancel(error)) {
        throw cancelRequest.getErrorObject()
      }
      throw convertResponseToErrorObject(_.get(error, 'response.data'), { useResponseMessage: true })
    })
    .then(response => {
      if (isErrorExist(response.data)) throw convertResponseToErrorObject(response.data, { useResponseMessage: true })
      return response.data
    })
    .then(({ data }) => ({
      courierServices: data.courierServices,
      next: _.get(data, 'courierServicesPagination.nextPage') > page
        ? () => fetchCartShipmentCourierServices({
          shipmentId,
          params: {
            page: page + 1,
            ...others
          },
          ...otherOptions
        })
        : null,
      all: page === 1 && _.get(data, 'courierServicesPagination.nextPage') > 1
        ? () => Promise.all(
          _.times(
            _.get(data, 'courierServicesPagination.totalPages') - 1,
            (index) => fetchCartShipmentCourierServices({
              shipmentId,
              params: {
                pageCountless: true,
                page: index + 2,
                ...others
              },
              ...otherOptions
            }),
          ),
        )
        : null,
      count: _.get(data, 'courierServicesPagination.totalCount'),
    }))
}

export function fetchCartShipmentAvailableDeliveryDates({
  shipmentId,
  params = {},
  ...otherOptions
}) {
  const source = axios.CancelToken.source()
  cancelRequest.add(
    'fetchCartShipmentAvailableDeliveryDates',
    () => source.cancel('fetchCartShipmentAvailableDeliveryDates: Operation cancelled by user')
  )

  return OmnitechApi
    .request({
      endpoint: `cart_shipments/${shipmentId}/available_delivery_dates`,
      method: 'get',
      params,
      cancelToken: source.token,
      ...otherOptions
    })
    .catch(error => {
      if (cancelRequest.isCancel(error)) {
        throw cancelRequest.getErrorObject()
      }
      throw convertResponseToErrorObject(_.get(error, 'response.data'), { useResponseMessage: true })
    })
    .then(response => {
      if (isErrorExist(response.data)) throw convertResponseToErrorObject(response.data, { useResponseMessage: true })
      return response.data
    })
    .then(({ data }) => ({
      dates: data.dates
    }))
}

export function fetchCartShipmentAvailableDeliveryTimeSlots({
  shipmentId,
  params = {},
  ...otherOptions
}) {
  const source = axios.CancelToken.source()
  cancelRequest.add(
    'fetchCartShipmentAvailableDeliveryTimeSlots',
    () => source.cancel('fetchCartShipmentAvailableDeliveryTimeSlots: Operation cancelled by user')
  )

  return OmnitechApi
    .request({
      endpoint: `cart_shipments/${shipmentId}/available_delivery_time_slots`,
      method: 'get',
      params,
      cancelToken: source.token,
      ...otherOptions
    })
    .catch(error => {
      if (cancelRequest.isCancel(error)) {
        throw cancelRequest.getErrorObject()
      }
      throw convertResponseToErrorObject(_.get(error, 'response.data'), { useResponseMessage: true })
    })
    .then(response => {
      if (isErrorExist(response.data)) throw convertResponseToErrorObject(response.data, { useResponseMessage: true })
      return response.data
    })
    .then(({ data }) => ({
      deliveryTimeSlots: data.deliveryTimeSlots
    }))
}
